import { Button, Icon } from '@aqarmap/aqarmap-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '~/components/Loader';
import {
  useGetListingDetailsQuery,
  useGetUserQuery,
  useListingRulesQuery,
  useListingStatusQuery,
  usePayListingFeesMutation,
  useTransferListingToParentMutation,
} from '~/redux/api/userApiSlice';
import { ListingDetailsType } from '~addListing/types';
import BuyPackageModal from '~addListing/pages/ListingPublishStep/BuyPackageModal';
import PublishAccountModal from '~addListing/pages/ListingPublishStep/PublishAccountModal';
import { closeModal, openModal } from '~/redux/features/uiState/uiStateSlice';
import { useAppDispatch } from '~/redux/hooks';
import DetailsItem from '~addListing/pages/ListingPublishStep/DetailsItem';
import ErrorBanner from '~addListing/pages/ListingPublishStep/ErrorBanner';
import MainAccountConfirmModal from '~addListing/pages/ListingPublishStep/MainAccountConfirmModal';
import { deleteListingData } from '~addListing/utils/LocalStorage';
import { isMainAccountConfirmSkipped } from '~addListing/utils/SessionStorage';
import useFullRoutes from '~/hooks/useFullRoutes';
import { UserPermissionsEnum } from '~/constants/enums';

const PublishAddListing = () => {
  const { t } = useTranslation('app');
  const { id, locale } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { ADD_LISTING_URL } = useFullRoutes();
  const {
    data,
    isSuccess: isListingDetailsSuccess,
    isFetching: isFetchingListingDetails,
    refetch,
  } = useGetListingDetailsQuery(id);
  const { data: listingRulesData, isLoading: isLoadingListingFees } =
    useListingRulesQuery(id);
  const { data: listingStatusData } = useListingStatusQuery(id);
  const [payListingFees, { isLoading }] = usePayListingFeesMutation();
  const [transferListingToParent, { isLoading: isTransferring }] =
    useTransferListingToParentMutation();
  const { data: userData, isLoading: isLoadingUserData } = useGetUserQuery();
  const [errorMessage, setErrorMessage] = useState('');

  const totalAmount = listingRulesData ? listingRulesData.total_amount : '';

  const listingDetails: ListingDetailsType = data?.listing;
  const getAttributeValue = (name: string) =>
    listingDetails?.attributes.find((item) => item.custom_field.name === name);
  const finishType = getAttributeValue('finish-type');
  const baths = getAttributeValue('baths');
  const rooms = getAttributeValue('rooms');

  const fees = listingRulesData?.fees;
  const availableBalance = listingRulesData?.available_balance;
  const canPayFees = availableBalance >= fees;
  const canSubAccountPayFees = Number(userData?.balance) >= fees;

  const allowPayAsYouGo = Boolean(
    !listingStatusData?.isEligibleForFreePublish &&
    listingStatusData?.isPayAsYouGoAllowed,
  );

  const redirectToListing = () => {
    deleteListingData(`${id}`);
    window.location.href = `${process.env.REACT_APP_BASE_URL}/${locale}/listing/${id}-${data?.listing?.section?.slug ?? listingDetails?.section?.slug}`;
  };

  const goToCheckout = () => {
    dispatch(closeModal('publishAccountModal'));
    dispatch(closeModal('mainAccountConfirmModal'));
    navigate(ADD_LISTING_URL.CHECKOUT(`${id}`));
  };

  const showError = (error: unknown) => {
    dispatch(closeModal('publishAccountModal'));
    dispatch(closeModal('mainAccountConfirmModal'));
    setErrorMessage(
      (error as { data?: { message?: string } })?.data?.message ||
        t('somethingWrong'),
    );
  };

  const payWithMain = async () => {
    const transferRes = await transferListingToParent(id);
    if ('error' in transferRes) return showError(transferRes.error);
    if (allowPayAsYouGo) return goToCheckout();

    const res = await payListingFees(id);
    if ('data' in res) redirectToListing();
    else showError(res.error);
  };

  const payWithSub = async () => {
    if (allowPayAsYouGo) return goToCheckout();

    const res = await payListingFees(id);
    if ('data' in res) redirectToListing();
    else showError(res.error);
  };

  const handleSubAccountPayFlow = () => {
    const hasActiveSubscription = userData?.user?.hasActiveSubscription;

    if (!hasActiveSubscription) {
      if (isMainAccountConfirmSkipped()) return payWithMain();
      return dispatch(openModal('mainAccountConfirmModal'));
    }
    return dispatch(openModal('publishAccountModal'));
  };
  const canSubAccountManageListings =
    userData?.user?.isSubAccount &&
    userData?.user?.sub_account_permissions?.includes(
      UserPermissionsEnum.MANAGE_LISTINGS,
    );

  const onPayAsYouGo = () => {
    if (canSubAccountManageListings) handleSubAccountPayFlow();
    else goToCheckout();
  };

  const onSubmit = () => {
    if (canSubAccountManageListings) handleSubAccountPayFlow();
    else if (canPayFees) payWithSub();
    else dispatch(openModal('buyPackageModal'));
  };

  useEffect(() => {
    if (!isFetchingListingDetails && !isListingDetailsSuccess)
      navigate(ADD_LISTING_URL.INITIALIZE);

    if (isListingDetailsSuccess) {
      const isOwner = listingDetails?.user.id === userData?.user?.id;
      const isPublisher =
        listingDetails?.publisher_user?.id === userData?.user?.id;
      if (!isOwner && !isPublisher) {
        navigate(ADD_LISTING_URL.INITIALIZE);
      }
    }
  }, [isListingDetailsSuccess, isFetchingListingDetails]);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (!errorMessage) return;
    const timer = setTimeout(() => setErrorMessage(''), 10000);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  if (isFetchingListingDetails || isLoadingListingFees || isLoadingUserData)
    return <Loader />;

  return (
    <div className="flex flex-col gap-4x w-full">
      {errorMessage && (
        <ErrorBanner onClose={() => setErrorMessage('')}>
          {errorMessage}
        </ErrorBanner>
      )}
      <p className="text-title_2">{t('publishListing')}</p>
      <div className="flex flex-col gap-4x md:gap-5x">
        <div className="flex flex-col lg:flex-row gap-2x md:gap-1.5x flex-1">
          <div className="w-full lg:w-[200px] h-[184px] lg:h-[128px] rounded-[12px] overflow-hidden border bg-gray border-gray flex justify-center items-center">
            {listingDetails?.photos.length === 0 ? (
              <Icon name="image-placeholder" size="64px" />
            ) : (
              <img
                src={listingDetails?.photos[0].photo.thumbnails.thumb}
                alt="listing"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div className="flex flex-col gap-2x">
            <div className="flex flex-col gap-x w-full truncate">
              <p className="text-title_4 truncate">
                {`${listingDetails.price?.toLocaleString()} ${t('egp')} ${listingDetails.section.title}`}
              </p>
              <p className="whitespace-pre-line text-body_2 text-gray__dark_2">
                {listingDetails?.title}
              </p>
            </div>
            <div className="flex flex-col gap-x">
              <div className="flex gap-x">
                <Icon name="location" className="text-gray__dark_2" />
                <p className="truncate text-body_2 text-gray__dark_2">
                  {listingDetails?.address}
                </p>
              </div>
              <div className="flex gap-2x flex-wrap">
                {Boolean(listingDetails?.area) && (
                  <DetailsItem
                    iconName="size"
                    text={`${listingDetails?.area} ${t('meter')}`}
                  />
                )}
                {Boolean(baths?.value) && (
                  <DetailsItem
                    iconName="bathroom"
                    text={`${baths?.value} ${t('rooms')}`}
                  />
                )}
                {Boolean(rooms?.value) && (
                  <DetailsItem
                    iconName="bedroom"
                    text={`${rooms?.value} ${t('rooms')}`}
                  />
                )}
                {Boolean(finishType?.value) && (
                  <DetailsItem
                    iconName="finish"
                    text={`${finishType?.value}`}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        {allowPayAsYouGo ? (
          <div className="bg-gray_light_1 p-1.5x md:p-2x rounded-[10px] flex justify-between items-center">
            <p className="text-body-2 text-gray__dark_2">
              <span>{t('listingCost')} : </span>
              <span className="text-title-4">{`${totalAmount ?? 0} `}</span>
              <span>{t('egp')}</span>
            </p>
            <Button
              onClick={onPayAsYouGo}
              isLoading={isLoading || isTransferring}
              variant="primary"
              size="medium"
              className="min-w-[120px] whitespace-nowrap"
            >
              {`${t('payNow')}`}
            </Button>
          </div>
        ) : (
          <div>
            <Button
              onClick={onSubmit}
              variant="primary"
              size="medium"
              className="min-w-[120px] whitespace-nowrap"
              isLoading={isLoading || isTransferring}
            >
              {canPayFees
                ? `${t('payNow')} (${fees ?? 0}  ${t('point')})`
                : `${t('publishNow')} (${fees ?? 0}  ${t('point')})`}
            </Button>
          </div>
        )}
      </div>
      <BuyPackageModal />
      <PublishAccountModal
        fees={fees}
        canPayFees={canSubAccountPayFees}
        isLoading={isLoading || isTransferring}
        onConfirm={(account) =>
          account === 'mainAccount' ? payWithMain() : payWithSub()
        }
      />
      <MainAccountConfirmModal
        fees={fees}
        isLoading={isLoading || isTransferring}
        onConfirm={payWithMain}
      />
    </div>
  );
};

export default PublishAddListing;
